import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Combobox, type ComboboxOption } from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { clubsControllerListClubsV1 } from '@/api/generated/clubs/clubs';
import {
  ClubsControllerListClubsV1SortBy,
  ClubsControllerListClubsV1SortOrder,
  type CatalogClubDto,
} from '@/api/generated/model';
import { Crest } from '@/shared/components/Crest';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { countryFlag, countryName } from '@/shared/lib/countries';
import { ANY } from '../lib/playersSearch';

/** Below this, a search matches most of the catalog and tells the user nothing. */
const MIN_SEARCH_LENGTH = 2;
const PAGE_SIZE = 20;

export interface ClubSelectProps {
  value?: string | undefined;
  onChange?: ((value: string | undefined) => void) | undefined;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  'aria-invalid'?: boolean;
  /**
   * Adds an explicit "show everything" option with this label, pinned to the
   * top of the list. Used on the filter bar, where clearing a club has to be a
   * visible choice; omitted in a form, where an empty club means a free agent
   * and is expressed by clearing the field.
   */
  anyLabel?: string;
  /** A label for the current value when it isn't in the loaded page — a form's initial club. */
  initialLabel?: string;
}

/**
 * A picker over the **global club catalog**.
 *
 * Searching is the server's job: the catalog spans every league and the
 * endpoint already matches on name, short name, city, country and league, so
 * the Combobox runs with `onSearch` — filtering the fetched page again locally
 * would hide rows the backend deliberately returned.
 *
 * Idle until the user types, because an unfiltered first page is an arbitrary
 * twenty clubs rather than a useful starting point. The catalog is seeded
 * reference data, hence the long `staleTime`.
 *
 * Note this picks a club from the catalog, not one the agency has added. A
 * player can be at any club in the world; whether the agency has a relationship
 * with it is a separate question, and requiring one here would make it
 * impossible to record where a player actually plays.
 */
export function ClubSelect({
  value,
  onChange,
  placeholder,
  className,
  disabled,
  id,
  anyLabel,
  initialLabel,
  ...aria
}: ClubSelectProps) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const debouncedTerm = useDebouncedValue(term);
  const trimmed = debouncedTerm.trim();

  const catalog = useQuery({
    queryKey: ['clubs', 'catalog', trimmed] as const,
    queryFn: ({ signal }) =>
      clubsControllerListClubsV1(
        {
          search: trimmed,
          limit: PAGE_SIZE,
          sortBy: ClubsControllerListClubsV1SortBy.name,
          sortOrder: ClubsControllerListClubsV1SortOrder.asc,
        },
        { signal },
      ),
    enabled: trimmed.length >= MIN_SEARCH_LENGTH,
    staleTime: 5 * 60_000,
    placeholderData: (previous) => previous,
  });

  /**
   * The club the user picked, remembered locally.
   *
   * Once something is selected the search text clears, so the next fetch
   * usually no longer contains that club — and without it among the options
   * the trigger would have no label to show. Holding onto the chosen club and
   * merging it back in is what keeps the label readable.
   */
  const [picked, setPicked] = useState<CatalogClubDto | null>(null);

  const rows = useMemo(
    () => (term.trim().length >= MIN_SEARCH_LENGTH ? (catalog.data?.data ?? []) : []),
    [catalog.data, term],
  );

  const options = useMemo<ComboboxOption[]>(() => {
    const merged =
      picked && picked.id === value && !rows.some((club) => club.id === value)
        ? [picked, ...rows]
        : rows;

    const clubOptions = merged.map((club) => ({
      value: club.id,
      label: club.name,
      icon: <Crest name={club.shortName ?? club.name} logo={club.logo} size={20} />,
      description: (
        <>
          {countryFlag(club.country) ? <span aria-hidden>{countryFlag(club.country)} </span> : null}
          {club.league?.name ?? countryName(club.country)}
        </>
      ),
    }));

    return anyLabel ? [{ value: ANY, label: anyLabel }, ...clubOptions] : clubOptions;
  }, [rows, picked, value, anyLabel]);

  // Three different reasons produce an empty list — "type something", "still
  // loading", "nothing matched" — and each needs different copy.
  const hint =
    term.trim().length < MIN_SEARCH_LENGTH
      ? t('players.clubSelect.typeToSearch', { min: MIN_SEARCH_LENGTH })
      : !catalog.isFetching && rows.length === 0
        ? t('players.clubSelect.noMatches', { term: term.trim() })
        : undefined;

  return (
    <Combobox
      id={id}
      aria-invalid={aria['aria-invalid']}
      className={className}
      disabled={disabled}
      // With an "all" option present the control always shows a value, so the
      // placeholder is only ever seen in a form.
      value={anyLabel ? (value ?? ANY) : value}
      onChange={(next) => {
        const nextId = next === ANY ? undefined : next;
        setPicked(rows.find((club) => club.id === nextId) ?? null);
        onChange?.(nextId);
      }}
      onSearch={setTerm}
      loading={catalog.isFetching && term.trim().length >= MIN_SEARCH_LENGTH}
      clearable={!anyLabel}
      hint={hint}
      options={options}
      selectedLabel={initialLabel}
      placeholder={placeholder ?? t('players.clubSelect.placeholder')}
      searchPlaceholder={t('players.clubSelect.placeholder')}
    />
  );
}
